/**
 * L-EST97 → WGS84 (эст. коническая проекция Ламберта, ГОСТ основы —
 * https://epsg.io/3301). Порт эталонного Python-конвертера, использованного
 * при подготовке набора публичных укрытий Päästeamet (24.09.2026):
 * $SCR/lest.py, сверен на Kultuurikatel (id PÕ70216) → [24.750897, 59.444685].
 *
 * Источник в CSV Päästeamet — колонки `lest_x` (northing, N) и `lest_y`
 * (easting, E); порядок в вызове lestToWgs84 такой же: (northing, easting).
 */

const A = 6378137.0
const F_FLAT = 1 / 298.257222101
const E = Math.sqrt(2 * F_FLAT - F_FLAT * F_FLAT)

const toRad = (deg: number): number => (deg * Math.PI) / 180
const toDeg = (rad: number): number => (rad * 180) / Math.PI

function mFn(phi: number): number {
  return Math.cos(phi) / Math.sqrt(1 - E * E * Math.sin(phi) ** 2)
}

function tFn(phi: number): number {
  return (
    Math.tan(Math.PI / 4 - phi / 2) / ((1 - E * Math.sin(phi)) / (1 + E * Math.sin(phi))) ** (E / 2)
  )
}

// Параметры проекции L-EST97 (стандартные параллели 59°20′ и 58°, базовая
// параллель 57°31′45.394″, центральный меридиан 24°, ложные координаты).
const PHI1 = toRad(59 + 20 / 60)
const PHI2 = toRad(58)
const PHI0 = toRad(57.51755393055556)
const LAMBDA0 = toRad(24)
const X0 = 500000.0
const Y0 = 6375000.0

const N = (Math.log(mFn(PHI1)) - Math.log(mFn(PHI2))) / (Math.log(tFn(PHI1)) - Math.log(tFn(PHI2)))
const CAPITAL_F = mFn(PHI1) / (N * tFn(PHI1) ** N)
const RHO0 = A * CAPITAL_F * tFn(PHI0) ** N

/**
 * L-EST97 (northing, easting) → WGS84 [lon, lat] (GeoJSON-порядок, как везде
 * в @app/geo). Итеративное уточнение широты — 10 шагов, сходится за 3–4.
 */
export function lestToWgs84(northing: number, easting: number): readonly [number, number] {
  const xp = easting - X0
  const yp = RHO0 - (northing - Y0)
  const rho = Math.sign(N) * Math.hypot(xp, yp)
  const tt = (rho / (A * CAPITAL_F)) ** (1 / N)
  const theta = Math.atan2(xp, yp)
  const lambda = theta / N + LAMBDA0
  let phi = Math.PI / 2 - 2 * Math.atan(tt)
  for (let i = 0; i < 10; i++) {
    phi =
      Math.PI / 2 -
      2 * Math.atan(tt * ((1 - E * Math.sin(phi)) / (1 + E * Math.sin(phi))) ** (E / 2))
  }
  return [toDeg(lambda), toDeg(phi)]
}
